import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const ExportServicesSection = () => {
  const { t } = useTranslation('home');
  const sliderRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const services = [
    {
      icon: 'ri-ship-line',
      title: t('services.service1.title'),
      description: t('services.service1.desc'),
      features: [
        t('services.service1.feature1'),
        t('services.service1.feature2'),
        t('services.service1.feature3'),
        t('services.service1.feature4'),
      ],
    },
    {
      icon: 'ri-store-3-line',
      title: t('services.service2.title'),
      description: t('services.service2.desc'),
      features: [
        t('services.service2.feature1'),
        t('services.service2.feature2'),
        t('services.service2.feature3'),
        t('services.service2.feature4'),
      ],
    },
    {
      icon: 'ri-truck-line',
      title: t('services.service3.title'),
      description: t('services.service3.desc'),
      features: [
        t('services.service3.feature1'),
        t('services.service3.feature2'),
        t('services.service3.feature3'),
        t('services.service3.feature4'),
      ],
    },
    {
      icon: 'ri-file-list-3-line',
      title: t('services.service4.title'),
      description: t('services.service4.desc'),
      features: [
        t('services.service4.feature1'),
        t('services.service4.feature2'),
        t('services.service4.feature3'),
        t('services.service4.feature4'),
      ],
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: t('services.service5.title'),
      description: t('services.service5.desc'),
      features: [
        t('services.service5.feature1'),
        t('services.service5.feature2'),
        t('services.service5.feature3'),
        t('services.service5.feature4'),
      ],
    },
    {
      icon: 'ri-customer-service-2-line',
      title: t('services.service6.title'),
      description: t('services.service6.desc'),
      features: [
        t('services.service6.feature1'),
        t('services.service6.feature2'),
        t('services.service6.feature3'),
        t('services.service6.feature4'),
      ],
    },
  ];

  const scrollToIndex = (index: number) => {
    if (!sliderRef.current) return;
    const cardWidth = sliderRef.current.offsetWidth;
    sliderRef.current.scrollTo({ left: cardWidth * index, behavior: 'smooth' });
    setActiveIndex(index);
  };

  const handleScroll = () => {
    if (!sliderRef.current) return;
    const cardWidth = sliderRef.current.offsetWidth;
    const scrollLeft = sliderRef.current.scrollLeft;
    const newIndex = Math.round(scrollLeft / cardWidth);
    setActiveIndex(newIndex);
  };

  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      id="services"
      className="py-10 sm:py-12 md:py-16 lg:py-24 xl:py-32 bg-gradient-to-b from-gray-950 to-black"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8 md:mb-12 lg:mb-16">
          <span className="inline-block px-3 py-1 sm:px-4 sm:py-1.5 bg-yellow-500/15 border border-yellow-400/30 rounded-full text-xs text-yellow-300 font-semibold uppercase tracking-wider mb-2 sm:mb-3 md:mb-4">
            {t('services.title')}
          </span>
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-2 sm:mb-3 md:mb-5 font-serif px-2">
            {t('services.sectionTitle')}{' '}
            <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent">
              {t('services.sectionTitleHighlight')}
            </span>
          </h2>
          <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed px-3 sm:px-2">
            {t('services.description')}
          </p>
        </div>

        {/* ── MOBILE: Horizontal Swipe Slider ── */}
        <div className="block sm:hidden">
          {/* Slider container */}
          <div
            ref={sliderRef}
            className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-0"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {services.map((service, i) => (
              <div
                key={i}
                className="flex-none w-full snap-center px-1"
              >
                <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 rounded-2xl p-5 border border-yellow-500/20 mx-1">
                  {/* Icon + title row */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex-shrink-0">
                      <i className={`${service.icon} text-xl text-black`}></i>
                    </div>
                    <h3 className="text-base font-bold text-white leading-tight">{service.title}</h3>
                  </div>
                  {/* Description */}
                  <p className="text-gray-400 text-sm leading-relaxed mb-4">{service.description}</p>
                  {/* Features */}
                  <ul className="grid grid-cols-2 gap-x-3 gap-y-2">
                    {service.features.map((f, j) => (
                      <li key={j} className="flex items-center gap-1.5 text-xs text-gray-300">
                        <i className="ri-check-line text-yellow-400 flex-shrink-0"></i>
                        {f}
                      </li>
                    ))}
                  </ul>
                  {/* Counter badge */}
                  <div className="mt-4 text-right">
                    <span className="text-xs text-yellow-500/60 font-medium">{i + 1} / {services.length}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dot indicators */}
          <div className="flex items-center justify-center gap-2 mt-4">
            {services.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollToIndex(i)}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  i === activeIndex
                    ? 'w-6 h-2 bg-yellow-400'
                    : 'w-2 h-2 bg-gray-600 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          {/* Prev / Next buttons */}
          <div className="flex items-center justify-between mt-3 px-1">
            <button
              onClick={() => scrollToIndex(Math.max(0, activeIndex - 1))}
              disabled={activeIndex === 0}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-yellow-500/30 text-yellow-400 text-xs font-medium disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all active:scale-95"
            >
              <i className="ri-arrow-left-s-line text-base"></i>
              Trước
            </button>
            <button
              onClick={() => scrollToIndex(Math.min(services.length - 1, activeIndex + 1))}
              disabled={activeIndex === services.length - 1}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-yellow-500/30 text-yellow-400 text-xs font-medium disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all active:scale-95"
            >
              Tiếp
              <i className="ri-arrow-right-s-line text-base"></i>
            </button>
          </div>
        </div>

        {/* ── DESKTOP: Grid layout (unchanged) ── */}
        <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          {services.map((service, i) => (
            <div
              key={i}
              className="group bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl p-5 md:p-6 lg:p-8 border border-yellow-500/15 hover:border-yellow-400/40 transition-all hover:shadow-xl hover:shadow-yellow-500/10 hover:-translate-y-1"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl mb-4 md:mb-6 group-hover:scale-110 transition-transform">
                <i className={`${service.icon} text-xl md:text-2xl text-black`}></i>
              </div>
              <h3 className="text-base md:text-lg lg:text-xl font-bold text-white mb-2.5 md:mb-3">{service.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4 md:mb-5">{service.description}</p>
              <ul className="space-y-1.5 md:space-y-2">
                {service.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-2 text-sm text-gray-300">
                    <i className="ri-check-line text-yellow-400 flex-shrink-0"></i>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExportServicesSection;
